import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Tag, User, ArrowLeft, Share2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

export const NoticiaDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [noticia, setNoticia] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadNoticia = async () => {
      try {
        const response = await fetch(`/data/news.json?t=${Date.now()}`, {
          cache: 'no-cache',
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        if (response.ok) {
          const data = await response.json();
          const foundNoticia = data.find(n => n.id === parseInt(id));
          if (foundNoticia) {
            setNoticia(foundNoticia);
          } else {
            setError('Noticia no encontrada');
          }
        } else {
          setError('Error al cargar la noticia');
        }
      } catch (error) {
        setError('Error al cargar la noticia');
      } finally {
        setLoading(false);
      }
    };

    loadNoticia();
  }, [id]);

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Convocatoria':
        return 'bg-blue-100 text-blue-800';
      case 'Evento':
        return 'bg-green-100 text-green-800';
      case 'Noticia':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: noticia.title,
          text: noticia.excerpt,
          url: window.location.href,
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      // Fallback: copiar URL al portapapeles
      navigator.clipboard.writeText(window.location.href);
      alert('URL copiada al portapapeles');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-burgundy-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando noticia...</p>
        </div>
      </div>
    );
  }

  if (!noticia) {
    return null; // El useEffect ya maneja la redirección
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Botón de regreso */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/noticias')}
            className="text-burgundy-700 hover:text-burgundy-800 hover:bg-burgundy-50"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver a Noticias
          </Button>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="overflow-hidden">
          {/* Header de la noticia */}
          <div className="bg-burgundy-50 p-6 border-b">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div className="flex items-center gap-2">
                <Badge className={getCategoryColor(noticia.category)}>
                  {noticia.category}
                </Badge>
                <div className="flex items-center text-sm text-gray-600">
                  <Calendar className="h-4 w-4 mr-1" />
                  <span>{formatDate(noticia.date)}</span>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleShare}
                className="border-burgundy-300 text-burgundy-700 hover:bg-burgundy-50"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Compartir
              </Button>
            </div>
            
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-burgundy-800 leading-tight">
              {noticia.title}
            </h1>
          </div>

          <CardContent className="p-0">
            {/* Imagen de la noticia */}
            <div className="h-64 md:h-80 bg-gradient-to-r from-burgundy-100 to-burgundy-200 flex items-center justify-center">
              <div className="text-center">
                <Tag className="h-16 w-16 text-burgundy-700 mx-auto mb-4" />
                <p className="text-burgundy-700 font-medium text-lg">
                  {noticia.category === 'Evento' ? 'Evento UPS' :
                   noticia.category === 'Convocatoria' ? 'Convocatoria Oficial' :
                   'Noticia UPS'}
                </p>
              </div>
            </div>

            {/* Contenido */}
            <div className="p-6 md:p-8">
              {/* Excerpt destacado */}
              <div className="mb-8">
                <p className="text-xl text-gray-700 leading-relaxed font-medium border-l-4 border-burgundy-600 pl-4 bg-gray-50 p-4 rounded-r">
                  {noticia.excerpt}
                </p>
              </div>

              {/* Contenido principal */}
              <div className="prose prose-lg max-w-none">
                <div className="space-y-6">
                  <p className="text-gray-700 leading-relaxed">
                    {noticia.content || 
                    `Esta es una noticia completa sobre "${noticia.title}". 
                    Aquí encontrarás toda la información detallada sobre este tema importante 
                    para la comunidad de Universidades Promotoras de la Salud.`}
                  </p>
                  
                  {/* Información específica por categoría */}
                  {noticia.category === 'Convocatoria' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 my-6">
                      <h3 className="font-semibold text-blue-800 mb-4 text-xl flex items-center">
                        <Tag className="h-5 w-5 mr-2" />
                        Información de la Convocatoria
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-blue-700">
                        <div>
                          <strong>Fecha límite:</strong> {noticia.deadline || 'Por definir'}
                        </div>
                        <div>
                          <strong>Dirigido a:</strong> Universidades miembros
                        </div>
                        <div>
                          <strong>Modalidad:</strong> Virtual y presencial
                        </div>
                        <div>
                          <strong>Requisitos:</strong> Consultar bases completas
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {noticia.category === 'Evento' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6 my-6">
                      <h3 className="font-semibold text-green-800 mb-4 text-xl flex items-center">
                        <Calendar className="h-5 w-5 mr-2" />
                        Detalles del Evento
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-green-700">
                        <div>
                          <strong>Fecha:</strong> {noticia.eventDate || formatDate(noticia.date)}
                        </div>
                        <div>
                          <strong>Modalidad:</strong> {noticia.modality || 'Por confirmar'}
                        </div>
                        <div>
                          <strong>Dirigido a:</strong> Comunidad universitaria
                        </div>
                        <div>
                          <strong>Registro:</strong> {noticia.registration || 'Próximamente'}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <p className="text-gray-700 leading-relaxed">
                    Para más información sobre esta {noticia.category.toLowerCase()}, 
                    ponte en contacto con nuestro equipo a través de los canales oficiales 
                    de comunicación de la Red de Universidades Promotoras de la Salud.
                  </p>
                </div>
              </div>

              {/* Footer con información del autor */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <div className="flex items-center space-x-1">
                      <User className="h-4 w-4" />
                      <span>Administrador UPS</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-4 w-4" />
                      <span>Publicado el {formatDate(noticia.date)}</span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleShare}
                      className="border-burgundy-300 text-burgundy-700 hover:bg-burgundy-50"
                    >
                      <Share2 className="h-4 w-4 mr-2" />
                      Compartir
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Botón de regreso en la parte inferior */}
        <div className="text-center mt-8">
          <Button
            onClick={() => navigate('/noticias')}
            className="bg-burgundy-700 hover:bg-burgundy-800 text-white"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver a todas las noticias
          </Button>
        </div>
      </div>
    </div>
  );
};